<template>
    <div class="stripe-payment-wrapper">
        <div :id="elementId"></div>
    </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

export default {
    name: 'StripePaymentElement',
    props: {
        baseUrl: {
            type: String,
            required: true,
        },
        // * DOM id the Stripe element mounts into. Two instances can be alive on one
        // * page — a checkout's own form and the Priority Physician Review offer's
        // * card entry — and `mount('#id')` takes the FIRST match, so the second
        // * instance must be given an id of its own or it renders into the first.
        elementId: {
            type: String,
            default: 'payment-element',
        },
        userId: {
            type: [String, Number, null],
            default: null,
        },
        userName: {
            type: String,
            default: '',
        },
        userEmail: {
            type: String,
            default: '',
        },
        // NOTE: When true, Stripe hides its own Country/ZIP billing fields.
        // The parent must then pass `billingAddress` so we can forward it to createPaymentMethod.
        hideAddressFields: {
            type: Boolean,
            default: false,
        },
        // Shape: { addressLine1, addressLine2, city, state, zip }
        billingAddress: {
            type: Object,
            default: null,
        },
        // * Opt-in to dark-mode theming. Only the dashboard uses dark mode —
        // * every other checkout/landing page must stay light regardless of
        // * any stale `data-theme="dark"` on <html> from a prior dashboard visit.
        enableDarkMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            paymentElement: null,
            hasCardInput: false,
            stripeReady: false,
            themeObserver: null,
        };
    },
    created() {
        // * Held on the instance, NOT in data(): one Stripe + Elements pair per
        // * component, so a second instance can't overwrite the first one's, and
        // * Vue never walks these objects making them reactive.
        this.stripeApi = null;
        this.stripeElements = null;
    },
    async mounted() {
        await this.initStripe();
        if (this.enableDarkMode) this.watchThemeChanges();
    },
    beforeDestroy() {
        if (this.themeObserver) this.themeObserver.disconnect();
    },
    methods: {
        getStripeAppearance() {
            const isDark = this.enableDarkMode
                && document.documentElement.getAttribute('data-theme') === 'dark';
            if (isDark) {
                return {
                    theme: 'night',
                    variables: {
                        colorPrimary: '#e9ff86',
                        colorBackground: '#242424',
                        colorText: '#f5f5f5',
                        colorTextSecondary: '#a3a3a3',
                        colorTextPlaceholder: '#737373',
                        colorDanger: '#f87171',
                        borderRadius: '12px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                    },
                    rules: {
                        '.Label': { color: '#a3a3a3', fontSize: '14px', fontWeight: '500' },
                        '.Input': { backgroundColor: '#242424', borderColor: '#3d3d3d', color: '#f5f5f5' },
                        '.Input:focus': { borderColor: '#e9ff86', boxShadow: '0 0 0 3px rgba(233, 255, 134, 0.15)' },
                        '.Tab': { backgroundColor: '#242424', borderColor: '#3d3d3d', color: '#f5f5f5' },
                        '.Tab--selected': { backgroundColor: '#2e2e2e', borderColor: '#e9ff86' },
                    },
                };
            }
            return {
                theme: 'stripe',
                variables: {
                    colorPrimary: '#08466f',
                    borderRadius: '12px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                },
            };
        },
        watchThemeChanges() {
            this.themeObserver = new MutationObserver(() => {
                if (this.stripeElements) {
                    this.stripeElements.update({ appearance: this.getStripeAppearance() });
                }
            });
            this.themeObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-theme'],
            });
        },
        async initStripe() {
            try {
                this.stripeApi = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
                this.stripeElements = this.stripeApi.elements({
                    mode: 'setup',
                    currency: 'usd',
                    paymentMethodTypes: ['card'],
                    paymentMethodCreation: 'manual',
                    appearance: this.getStripeAppearance(),
                });
                this.$nextTick(() => {
                    this.createPaymentElement();
                });
            } catch (error) {
                console.error('Failed to initialize Stripe:', error);
                this.$emit('error', 'Failed to initialize payment. Please refresh the page.');
            }
        },
        createPaymentElement() {
            if (!this.stripeElements) return;

            if (this.paymentElement) {
                this.paymentElement.mount(`#${this.elementId}`);
                return;
            }

            const elementOptions = {
                layout: 'tabs',
                defaultValues: {
                    billingDetails: {
                        address: {
                            country: 'US',
                        },
                    },
                },
                wallets: {
                    applePay: 'never',
                    googlePay: 'never',
                    link: 'never',
                },
                terms: {
                    card: 'never',
                },
            };
            if (this.hideAddressFields) {
                // * Parent collects address separately — hide Stripe's Country/ZIP fields.
                elementOptions.fields = { billingDetails: { address: 'never' } };
            }
            this.paymentElement = this.stripeElements.create('payment', elementOptions);
            this.paymentElement.mount(`#${this.elementId}`);
            this.paymentElement.on('change', (event) => {
                this.hasCardInput = event.complete;
                this.$emit('ready', event.complete);
            });
            this.stripeReady = true;
        },
        remount() {
            if (this.paymentElement) {
                this.$nextTick(() => {
                    this.paymentElement.mount(`#${this.elementId}`);
                });
            }
        },
        async confirmPayment() {
            if (!this.stripeApi || !this.stripeElements) {
                this.$emit('error', 'Payment not initialized. Please refresh the page.');
                return null;
            }

            try {
                // Validate the Payment Element
                const { error: submitError } = await this.stripeElements.submit();
                if (submitError) {
                    this.$emit('error', submitError.message || 'Please check your card details.');
                    return null;
                }

                // Create PaymentMethod directly — no SetupIntent needed
                const billingDetails = {
                    name: this.userName || '',
                    email: this.userEmail || '',
                };
                // ! When hideAddressFields is true, we MUST pass address here (Stripe uses it for AVS/ZIP).
                if (this.billingAddress) {
                    billingDetails.address = {
                        line1: this.billingAddress.addressLine1 || '',
                        line2: this.billingAddress.addressLine2 || '',
                        city: this.billingAddress.city || '',
                        state: this.billingAddress.state || '',
                        postal_code: this.billingAddress.zip || '',
                        country: 'US',
                    };
                }
                const { paymentMethod, error } = await this.stripeApi.createPaymentMethod({
                    elements: this.stripeElements,
                    params: { billing_details: billingDetails },
                });

                if (error) {
                    this.$emit('error', error.message || 'Invalid card details. Please check your card information.');
                    return null;
                }

                // Emit the pm_ token + card details (2nd arg is optional; existing consumers ignore it)
                const cardDetails = paymentMethod.card ? {
                    number: `XXXXXXXXXXXX${paymentMethod.card.last4}`,
                    expiration_month: paymentMethod.card.exp_month,
                    expiration_year: paymentMethod.card.exp_year,
                    cardBin: '',
                    cardBrand: paymentMethod.card.brand,
                    cardLast4: paymentMethod.card.last4,
                } : null;
                this.$emit('token', paymentMethod.id, cardDetails);
                return paymentMethod.id;
            } catch (error) {
                console.error(error);
                this.$emit('error', 'Invalid card details. Please check your card information.');
                return null;
            }
        },
        resetElement() {
            // Re-create elements and payment element for retry
            try {
                this.stripeElements = this.stripeApi.elements({
                    mode: 'setup',
                    currency: 'usd',
                    paymentMethodTypes: ['card'],
                    paymentMethodCreation: 'manual',
                });
                this.paymentElement = null;
                this.$nextTick(() => {
                    this.createPaymentElement();
                });
            } catch (error) {
                console.error('Failed to reset payment:', error);
            }
        },
    },
};
</script>

<style scoped>
.stripe-payment-wrapper {
    width: 100%;
}
</style>
